/**
 * @voltxampere/engine — Per-iteration MNA assembly
 *
 * SPECS §3.2 spells out the build loop:
 *
 *   Y, rhs ← zero
 *   for each component:
 *     stamp.apply(Y, rhs, component, t, xPrev)
 *
 * Sprint 5's wrinkle is the SPECS §3.6 / §3.7 fallbacks: gmin gets
 * added to every diagonal of the admittance block (not the
 * augmented rows — pure ground-to-node regularisation), and source
 * stepping is applied inside the source stamps via `ctx.sourceScale`.
 *
 * The builder accumulates COO triplets via `ctx.addY` and converts
 * to CSR with `createSparse(N+M, N+M, triplets)`. Sprint 4's
 * `createSparse` already sums duplicates — exactly the behaviour
 * MNA stamping wants (one matrix cell often gets contributions from
 * multiple components).
 *
 * Returned object pairs the sparse matrix with the RHS plus a
 * convenience handle on the underlying triplet buffer (kept for
 * tests so they can introspect the accumulated stamps).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { createSparse } from "@voltxampere/math";
import { createDCStampContext } from "../stamps/context.js";
import { getDCStampOrThrow } from "../stamps/register.js";
export function buildMNA(circuit, plan, opts) {
    const { size, nodeCount, internalNodeCount } = plan;
    const t = opts.t ?? 0;
    const gmin = opts.gmin ?? 0;
    const sourceScale = opts.sourceScale ?? 1;
    if (gmin < 0 || !Number.isFinite(gmin)) {
        throw new ContractViolationError(`buildMNA: gmin must be a non-negative finite number, got ${String(gmin)}`);
    }
    if (!Number.isFinite(sourceScale)) {
        throw new ContractViolationError(`buildMNA: sourceScale must be finite, got ${String(sourceScale)}`);
    }
    if (opts.xPrev.length !== size) {
        throw new ContractViolationError(`buildMNA: xPrev length ${String(opts.xPrev.length)} ≠ N+I+M ${String(size)}`);
    }
    const triplets = [];
    const rhs = new Float64Array(size);
    const ctx = createDCStampContext({
        nodeCount,
        internalNodeCount,
        internalNodeIndices: plan.internalNodeIndices,
        vSourceCount: plan.vSourceCount,
        netToIndex: plan.netToIndex,
        groundNetId: plan.groundNetId,
        vSourceIdToIndex: plan.vSourceIdToIndex,
        triplets,
        rhs,
        xPrev: opts.xPrev,
        t,
        gmin,
        sourceScale,
        junctionHistory: opts.junctionHistory ?? new Map(),
        enableDeltaVDamping: opts.enableDeltaVDamping ?? true,
        ...(opts.temperature !== undefined ? { temperature: opts.temperature } : {}),
    });
    for (const c of circuit.components) {
        const stamp = getDCStampOrThrow(c.type);
        stamp(ctx, c);
    }
    // Regularise the admittance block. SPECS §3.6 adds gmin from each
    // node-to-ground; equivalent at the matrix level to bumping every
    // diagonal in the [0..N+I) range. Sprint 24 B1: internal nodes
    // (BJT parazitik B'/C'/E') obey ordinary KCL just like external
    // nets, so they get the same regularisation treatment.
    if (gmin > 0) {
        const kclEnd = nodeCount + internalNodeCount;
        for (let i = 0; i < kclEnd; i++) {
            triplets.push({ row: i, col: i, value: gmin });
        }
    }
    const Y = createSparse(size, size, triplets);
    return { Y, rhs, triplets };
}
//# sourceMappingURL=mna-builder.js.map